import dayjs from '../../utils/dayjs';
import React, { useState } from 'react';
import { Badge, Button, ButtonGroup, Card, DataTable, EmptyState, Layout, Link, Page, Text } from '@shopify/polaris';
import { ChargePoint, useApprovalChargeStation, useGetChargeStations } from 'queries/charge_station.query';
import { Helmet } from 'react-helmet-async';
import emptyIMG from 'media/images/empty.png';

import SkeletonLoading from 'components/skeletonPageLoading';
import { useQueryClient } from '@tanstack/react-query';
import ChargeStationModal from './charge_station_modal';
import { useNavigate } from 'react-router-dom';

export default function ChargeStationList() {
  const { data: chargeStationData, isLoading, isSuccess } = useGetChargeStations();
  const queryClient = useQueryClient();
  const [approvingId, setApprovingId] = useState<number | null>(null);
  const { mutateAsync: approvalChargeStation } = useApprovalChargeStation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentChargeStationId, setCurrentChargeStationId] = useState<number | null>(null);

  const chargeStations = chargeStationData ?? [];

  const renderStatusBadge = (status?: ChargePoint['status']) => {
    switch (status) {
      case 'APPROVED':
        return <Badge tone="success">Đã duyệt</Badge>;
      case 'DENIED':
        return <Badge tone="critical">Từ chối</Badge>;
      case 'UNAPPROVED':
        return <Badge tone="warning">Chưa duyệt</Badge>;
      default:
        return <Badge tone="new">-</Badge>;
    }
  };

  const renderOnlineBadge = (isOnline?: boolean) => (isOnline ? <Badge tone="success">Online</Badge> : <Badge tone="critical">Offline</Badge>);

  const handleApproval = async (chargeStationId: number, decision: 'approve' | 'deny') => {
    setApprovingId(chargeStationId);
    try {
      await approvalChargeStation({ chargeStationId, decision });
      await queryClient.invalidateQueries({ queryKey: ['charge_station/fetch_entities'] });
    } finally {
      setApprovingId(null);
    }
  };

  const handleOpenCreate = () => {
    setCurrentChargeStationId(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (chargeStationId: number) => {
    setCurrentChargeStationId(chargeStationId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentChargeStationId(null);
  };

  const history = useNavigate();

  const renderActions = (station: ChargePoint) => {
    if (!station.id) {
      return (
        <Text as="span" tone="subdued">
          -
        </Text>
      );
    }

    return (
      <ButtonGroup>
        <Button size="slim" onClick={() => handleOpenEdit(station.id as number)}>
          Chỉnh sửa
        </Button>
        {station.status === 'UNAPPROVED' ? (
          <>
            <Button
              size="slim"
              variant="primary"
              loading={approvingId === station.id}
              onClick={() => handleApproval(station.id as number, 'approve')}
            >
              Duyệt
            </Button>
            <Button size="slim" tone="critical" loading={approvingId === station.id} onClick={() => handleApproval(station.id as number, 'deny')}>
              Từ chối
            </Button>
          </>
        ) : null}
      </ButtonGroup>
    );
  };

  const rows = chargeStations.map((station) => [
    station.id ?? '-',
    <Link
      onClick={() => {
        history(`/charge_station/view/${station.id}`);
      }}
    >
      {station.cpid ?? '-'}
    </Link>,
    station.name ?? '-',
    station.address ?? '-',
    station.gunCount ?? station.connectors?.length ?? '-',
    station.maxPowerKw ? `${station.maxPowerKw} kW` : '-',
    station.hotline ?? '-',
    renderStatusBadge(station.status),
    renderOnlineBadge(station.onlineStatus),
    station.updatedAt ? dayjs(new Date(station.updatedAt)).format('HH:mm:ss DD-MM-YYYY') : '-',
    renderActions(station),
  ]);

  const emptyState = (
    <EmptyState heading="Không có trạm sạc nào!" image={emptyIMG}>
      <p>Chưa có dữ liệu trạm sạc. Hãy tạo mới hoặc thử lại sau.</p>
    </EmptyState>
  );

  if (isLoading) {
    return <SkeletonLoading />;
  }

  return (
    <Page
      title="Quản lý trạm sạc"
      primaryAction={{
        content: 'Thêm trạm sạc',
        onAction: handleOpenCreate,
      }}
    >
      <Helmet>
        <title>Quản lý trạm sạc</title>
      </Helmet>
      <Layout>
        <Layout.Section>
          <Card padding="0">
            {isSuccess && chargeStations.length > 0 ? (
              <DataTable
                hoverable
                verticalAlign="middle"
                fixedFirstColumns={2}
                columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'text']}
                headings={[
                  'ID',
                  'CPID',
                  'Tên trạm',
                  'Địa chỉ',
                  'Số cổng sạc',
                  'Công suất',
                  'Hotline',
                  'Trạng thái duyệt',
                  'Online',
                  'Cập nhật',
                  'Thao tác',
                ]}
                rows={rows}
                hideScrollIndicator
              />
            ) : (
              emptyState
            )}
          </Card>
        </Layout.Section>
      </Layout>
      <ChargeStationModal onClose={handleCloseModal} show={isModalOpen} currentChargeStationId={currentChargeStationId} />
    </Page>
  );
}

